import React from 'react';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FolderCopyIcon from '@mui/icons-material/FolderCopy';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useGlobalState } from '../../global/context/global-context';
import { adminUrl, gedUrl, isLocalhost, ouvUrl, scpUrl, serUrl, sgeUrl, sicUrl } from '../../../utils/environment';

export type SystemKey = 'ouv' | 'sic' | 'sge' | 'scp' | 'ser' | 'ged' | 'apm';

// Sistemas ainda não liberados: aparecem só rodando local e ficam ocultos no sandbox e em produção
const LOCAL_ONLY_SYSTEMS: SystemKey[] = ['ged'];

export interface SystemLink {
  key: SystemKey;
  /** Nome do produto no padrão "GovHub-<Sistema>". */
  label: string;
  /** O que é o sistema e para que serve. */
  description: string;
  /** Cor principal do módulo (a mesma do tema dele no applications). */
  color: string;
  href: string;
  icon: React.ReactElement;
}

/**
 * Sistemas GovHub exibidos no menu "Sistemas GovHub" da barra utilitária, no menu mobile e no
 * mapa do site (abrem em nova aba).
 */
export const useSystemLinks = (): SystemLink[] => {
  const { applicationData } = useGlobalState();
  const entity = applicationData?.entity;

  const links: SystemLink[] = [
    {
      key: 'sic',
      label: 'GovHub-SIC',
      color: '#A67C00',
      description: 'Serviço de Informação ao Cidadão: peça informações públicas, acompanhe a resposta e consulte pedidos já respondidos.',
      href: sicUrl(entity),
      icon: <InfoOutlinedIcon fontSize="small" />,
    },
    {
      key: 'ouv',
      label: 'GovHub-Ouvidoria',
      color: '#D84315',
      description: 'Canal do cidadão para registrar e acompanhar denúncias, reclamações, sugestões e elogios.',
      href: ouvUrl(entity),
      icon: <RecordVoiceOverIcon fontSize="small" />,
    },
    {
      key: 'sge',
      label: 'GovHub-Emendas',
      color: '#2E7D32',
      description: 'Transparência das emendas parlamentares: valores, beneficiários e andamento da execução.',
      href: sgeUrl(entity),
      icon: <AccountBalanceWalletIcon fontSize="small" />,
    },
    {
      key: 'ser',
      label: 'GovHub-Remessas',
      color: '#3949AB',
      description: 'Envio das publicações oficiais ao Diário Oficial e acompanhamento de cada remessa.',
      href: serUrl(entity),
      icon: <CloudUploadIcon fontSize="small" />,
    },
    {
      key: 'scp',
      label: 'GovHub-Contratações',
      color: '#2196f3',
      description: 'Gestão das contratações públicas, com publicação e integração ao PNCP.',
      href: scpUrl(entity),
      icon: <AssignmentIcon fontSize="small" />,
    },
    {
      key: 'apm',
      label: 'GovHub-Portal',
      color: '#00897B',
      description: 'Área administrativa do portal: menus, páginas, notícias e dados de transparência.',
      href: adminUrl(entity),
      icon: <AdminPanelSettingsIcon fontSize="small" />,
    },
    {
      key: 'ged',
      label: 'GovHub-Documentos Eletrônicos',
      color: '#455A64',
      description: 'Gestão eletrônica de documentos: digitalização, busca por conteúdo, guarda e temporalidade dos arquivos.',
      href: gedUrl(entity),
      icon: <FolderCopyIcon fontSize="small" />,
    },
  ];

  return links.filter((link) => isLocalhost() || !LOCAL_ONLY_SYSTEMS.includes(link.key));
};
